import { z } from 'zod';

const deadlineItemSchema = z.preprocess((val) => {
  if (val && typeof val === 'object' && 'id' in val && 'label' in val) {
    const item = val as any;
    return {
      id: String(item.id || ''),
      label: String(item.label || ''),
      checked: Boolean(item.checked),
      value: item.value ? String(item.value) : undefined,
    };
  }

  return { id: '', label: '', checked: false, value: undefined };
}, z.object({
  id: z.string(),
  label: z.string(),
  checked: z.boolean(),
  value: z.string().optional(),
}));

const deadlinesSchema = z.preprocess((val) => {
  if (val && typeof val === 'object' && 'internal' in val && 'external' in val) {
    const deadlines = val as any;
    return {
      internal: Array.isArray(deadlines.internal) ? deadlines.internal : [],
      external: Array.isArray(deadlines.external) ? deadlines.external : [],
    };
  }

  return { internal: [], external: [] };
}, z.object({
  internal: z.array(deadlineItemSchema),
  external: z.array(deadlineItemSchema),
}));

const heatTreatmentSchema = z.preprocess((val) => {
  if (val && typeof val === 'object') {
    const treatment = val as any;
    return {
      tensionRelief: Boolean(treatment.tensionRelief),
      performance: Boolean(treatment.performance),
      aging: Boolean(treatment.aging),
      normalizationAndTempering: Boolean(treatment.normalizationAndTempering),
      annealing: Boolean(treatment.annealing),
      solutionTreatment: Boolean(treatment.solutionTreatment),
      quenchingAndTempering: Boolean(treatment.quenchingAndTempering),
    };
  }

  return {
    tensionRelief: false,
    performance: false,
    aging: false,
    normalizationAndTempering: false,
    annealing: false,
    solutionTreatment: false,
    quenchingAndTempering: false,
  };
}, z.object({
  tensionRelief: z.boolean(),
  performance: z.boolean(),
  aging: z.boolean(),
  normalizationAndTempering: z.boolean(),
  annealing: z.boolean(),
  solutionTreatment: z.boolean(),
  quenchingAndTempering: z.boolean(),
}));

const optionalNumber = z.preprocess((val) => {
  if (val === null || val === '' || val === undefined || val === 'null') {
    return undefined;
  }
  if (typeof val === 'number' && !isNaN(val)) {
    return val;
  }
  if (typeof val === 'string') {
    const num = Number(val.trim());
    return isNaN(num) ? undefined : num;
  }
  return undefined;
}, z.number().optional());

const priceDetailsSchema = z.object({
  pricePerKgMin: optionalNumber,
  pricePerKgFinal: optionalNumber,
  unitPrice: optionalNumber,
  itemSubtotal: optionalNumber,
  itemTaxes: optionalNumber,
  serviceUnit: z.string().optional(),
  discount: z.preprocess((val) => {
    if (val === '' || val === null || val === undefined || val === 'null') return undefined;
    const num = Number(String(val).replace(',', '.'));
    return isNaN(num) ? undefined : num;
  }, z.number().min(0, "Desconto não pode ser negativo").optional()),
  deliveryDays: z.string().optional(),
});

const requestedProductMaterialSchema = z.object({
  product: z.string().optional(),
  format: z.string().optional(),
  diameter: optionalNumber,
  thickness: optionalNumber,
  length: optionalNumber,
  finishing: z.string().optional(),
  weigth: z.preprocess(
    (val) => {
      if (val === null || val === undefined || val === '' || val === 'null') {
        return undefined;
      }
      if (typeof val === 'string') {
        const trimmedVal = val.trim().toLowerCase();
        if (trimmedVal === 'auto') return 'Auto';
        if (trimmedVal === '') return undefined;
        const num = Number(val);
        return isNaN(num) ? undefined : num;
      }
      if (typeof val === 'number' && !isNaN(val)) {
        return val;
      }
      return undefined;
    },
    z.union([z.literal('Auto'), z.number()]).optional()
  ),
  quantity: optionalNumber,
  quantityPieces: optionalNumber,
  ncm: z.string().optional(),
  specs: z.string().optional(),
});

const offeredProductMaterialSchema = z.object({
  product: z.string().optional(),
  format: z.string().optional(),
  diameter: optionalNumber,
  thickness: optionalNumber,
  length: optionalNumber,
  finishing: z.string().optional(),
  weigth: z.preprocess(
    (val) => {
      if (val === null || val === undefined || val === '' || val === 'null') {
        return undefined;
      }
      if (typeof val === 'string') {
        const trimmedVal = val.trim().toLowerCase();
        if (trimmedVal === 'auto') return 'Auto';
        if (trimmedVal === '') return undefined;
        const num = Number(val);
        return isNaN(num) ? undefined : num;
      }
      if (typeof val === 'number' && !isNaN(val)) {
        return val;
      }
      return undefined;
    },
    z.union([z.literal('Auto'), z.number()]).optional()
  ),
  quantity: z.preprocess(
    (val) => {
      if (val === null || val === undefined || val === '' || val === 'null') {
        return undefined;
      }
      if (typeof val === 'number' && !isNaN(val)) {
        return val;
      }
      if (typeof val === 'string') {
        const num = Number(val.trim());
        return isNaN(num) ? undefined : num;
      }
      return undefined;
    },
    z.number().optional()
  ),
  ncm: z.string().optional(),
  specs: z.string().optional(),
});

const finalProductSchema = z.object({
  produto: z.string().optional(),
  codigo: z.string().optional(),
  nomeCustomizado: z.string().optional(),
  tipoProduto: z.string().optional(),
  fabricante: z.string().optional(),
  script: z.string().optional(),
  quantidade: optionalNumber,
  quantidadePecas: optionalNumber,
  anexos: z.array(z.string()).optional(),
  observacoes: z.string().optional(),
});

const resourceSchema = z.object({
  insumo: z.string().optional(),
  categoria: z.string().optional(),
  tipo: z.string().optional(),
  fornecedor: z.string().optional(),
  quantidade: optionalNumber,
  quantidadePecas: optionalNumber,
  anexos: z.array(z.string()).optional(),
  observacoes: z.string().optional(),
});

const stageSchema = z.object({
  servico: z.string().optional(),
  categoria: z.string().optional(),
  tipo: z.string().optional(),
  fornecedor: z.string().optional(),
  quantidade: optionalNumber,
  quantidadePecas: optionalNumber,
  anexos: z.array(z.string()).optional(),
  observacoes: z.string().optional(),
});

const feedstockSchema = z.object({
  materiaPrima: z.string().optional(),
  quantidade: optionalNumber,
  quantidadePecas: optionalNumber,
  anexos: z.array(z.string()).optional(),
  observacoes: z.string().optional(),
  stockDescription: z.string().optional(),
});

export const itemOrderSchema: z.ZodType<any> = z.lazy(() => z.object({
  itemType: z.enum(["SERVICE", "RESOURCE", "MATERIAL", "PRODUCT"], {
    required_error: "Tipo de item é obrigatório"
  }),
  requestedProductMaterial: requestedProductMaterialSchema.optional(),
  offeredProductMaterial: offeredProductMaterialSchema.optional(),
  finalProduct: finalProductSchema.optional(),
  resource: resourceSchema.optional(),
  stage: stageSchema.optional(),
  feedstock: feedstockSchema.optional(),
  deadlines: deadlinesSchema.optional(),
  heatTreatments: heatTreatmentSchema.optional(),
  priceDetails: priceDetailsSchema.optional(),
  isSaved: z.boolean().optional().default(false),
  isPrincipal: z.boolean().optional().default(false),
  parentId: optionalNumber,
  subItems: z.array(itemOrderSchema).optional().default([]),
  isContainer: z.boolean().optional().default(false),
  materialId: optionalNumber,
  productId: optionalNumber,
  serviceId: optionalNumber,
  stageId: optionalNumber,
  resourceId: optionalNumber,
  serviceCategoryId: optionalNumber,
  serviceTypeId: optionalNumber,
  resourceCategoryId: optionalNumber,
  resourceTypeId: optionalNumber,
  serviceSupplierCompanyId: optionalNumber,
  resourceSupplierCompanyId: optionalNumber,
  scriptId: optionalNumber,
  stockId: optionalNumber,
  ncmId: optionalNumber,
  quantity: optionalNumber
}).superRefine((data, ctx) => {
  if (data.isContainer) {
    if (!data.subItems || data.subItems.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Um container deve ter pelo menos um sub-item.",
        path: ['subItems']
      });
    }
    return;
  }

  if (data.isPrincipal || !data.parentId) {
    if (data.itemType === "MATERIAL") {
      if (!data.feedstock) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Detalhes da Matéria-Prima são obrigatórios.", path: ['feedstock'] });
      } else {
        const result = feedstockSchema.safeParse(data.feedstock);
        if (!result.success) {
          result.error.errors.forEach(err => { ctx.addIssue({ code: z.ZodIssueCode.custom, message: err.message, path: ['feedstock', ...err.path] }); });
        }
      }

      if (data.requestedProductMaterial) {
        const result = requestedProductMaterialSchema.safeParse(data.requestedProductMaterial);
        if (!result.success) {
          result.error.errors.forEach(err => { ctx.addIssue({ code: z.ZodIssueCode.custom, message: err.message, path: ['requestedProductMaterial', ...err.path] }); });
        }
      }

      if (data.offeredProductMaterial) {
        const result = offeredProductMaterialSchema.safeParse(data.offeredProductMaterial);
        if (!result.success) {
          result.error.errors.forEach(err => { ctx.addIssue({ code: z.ZodIssueCode.custom, message: err.message, path: ['offeredProductMaterial', ...err.path] }); });
        }
      }
      if (!data.deadlines) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Prazos são obrigatórios para 'Materia Prima'.", path: ['deadlines'] });
      }
      if (!data.heatTreatments) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Tratamentos Térmicos são obrigatórios para 'Materia Prima'.", path: ['heatTreatments'] });
      }
    }

    if (data.itemType === "PRODUCT") {
      if (!data.finalProduct) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Detalhes do Produto Final são obrigatórios.", path: ['finalProduct'] });
      } else {
        const result = finalProductSchema.safeParse(data.finalProduct);
        if (!result.success) {
          result.error.errors.forEach(err => { ctx.addIssue({ code: z.ZodIssueCode.custom, message: err.message, path: ['finalProduct', ...err.path] }); });
        }
      }
    }

    if (data.itemType === "RESOURCE") {
      if (!data.resource) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Detalhes do Recurso são obrigatórios.", path: ['resource'] });
      } else {
        const result = resourceSchema.safeParse(data.resource);
        if (!result.success) {
          result.error.errors.forEach(err => { ctx.addIssue({ code: z.ZodIssueCode.custom, message: err.message, path: ['resource', ...err.path] }); });
        }
      }
    }

    if (data.itemType === "SERVICE") {
      if (!data.stage) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Detalhes do Estágio são obrigatórios.", path: ['stage'] });
      } else {
        const result = stageSchema.safeParse(data.stage);
        if (!result.success) {
          result.error.errors.forEach(err => { ctx.addIssue({ code: z.ZodIssueCode.custom, message: err.message, path: ['stage', ...err.path] }); });
        }
      }
    }
  }

  if (data.parentId && !data.isPrincipal) {
    if (data.itemType === "PRODUCT" && !data.finalProduct) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Detalhes do Produto Final são obrigatórios.", path: ['finalProduct'] });
    }
    if (data.itemType === "RESOURCE" && !data.resource) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Detalhes do Recurso são obrigatórios.", path: ['resource'] });
    }
    if (data.itemType === "SERVICE" && !data.stage) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Detalhes do Estágio são obrigatórios.", path: ['stage'] });
    }
    if (data.itemType === "MATERIAL" && !data.feedstock) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Detalhes da Matéria-Prima são obrigatórios.", path: ['feedstock'] });
    }
  }
}));

export const installmentSchema = z.object({
  numero: z.number(),
  valor: z.number(),
  vencimento: z.string(),
  comissaoAtiva: z.boolean().optional().default(false),
  comissaoPercentual: z.number().optional(),
  diasOffset: z.number().optional(),
});

export const orderSchema = z.object({
  id: z.number().optional(),
  supplier: z.string().optional(),
  supplierId: z.number().optional(),
  resource: z.string().optional(),
  seller: z.string().optional(),
  sellerId: z.number().optional(),
  client: z.string().optional(),
  clientId: z.number().optional(),
  orderType: z.string().optional(),
  orderDate: z.string().optional(),
  deliveryDate: z.string().optional(),
  paymentConditionId: optionalNumber,
  paymentConditionName: z.string().optional(),
  // Forma de pagamento (catálogo global PaymentMethod). Campos transientes do front;
  // o submit monta `paymentMethods: [{ paymentMethodId, description? }]` (M2M).
  paymentMethodId: optionalNumber,
  paymentMethodName: z.string().optional(),
  paymentMethodDescription: z.string().optional(),
  shippingTypeId: optionalNumber,
  shippingTypeName: z.string().optional(),
  shippingValue: optionalNumber,
  trackingCode: z.string().optional(),
  fileIds: z.array(z.number()).optional(),
  attachments: z.array(z.string()).optional(),
  criticalAnalysis: z.boolean().optional(),
  criticalAnalysisDesc: z.string().optional(),
  extraInfo: z.string().optional(),
  items: z.array(itemOrderSchema)
    .min(1, "O pedido deve conter pelo menos um item."),
  quantity: z.number().optional(),
  totalQuantity: z.number().optional(),
  taxes: optionalNumber,
  total: optionalNumber,
  installments: z.array(installmentSchema).optional(),
  clientDetails: z.record(z.any()).optional(),
  sellerDetails: z.record(z.any()).optional(),
  supplierDetails: z.record(z.any()).optional(),
  status: z.boolean().optional().nullable(),
  closedBy: z.string().optional().nullable(),
  closedByDetails: z.record(z.any()).optional().nullable(),
});

export type OrderSchemaType = z.infer<typeof orderSchema>;
export type ItemOrderSchemaType = z.infer<typeof itemOrderSchema>;
export type ProductMaterialDetailSchemaType = z.infer<typeof offeredProductMaterialSchema>;

export type ItemPath = `items.${number}` | `items.${number}.subItems.${number}`;